import {
  ResponseResult,
  ResponseStatus,
  ResponseType,
  type QuestionResponseDetails,
  type SubResponse,
  type SubQuestionResponseEntry,
  type UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import { scoredDefs, type SubQuestionTypeRef } from './deriveParentResult';
import { deriveSubResponsesStatus } from './deriveSubResponsesStatus';
import {
  buildPreservedResponseData,
  projectSubResponsesForPreservation,
} from './preserveResponseFields';

/**
 * Arguments for {@link buildSubQuestionValueUpdate}. Bundled in a
 * single object so future fields (per-sub attachments,
 * `expectedLastUpdatedAt`) slot in without changing the caller
 * signature — same shape convention as
 * {@link buildSubQuestionAnswerUpdate}.
 */
export interface BuildSubQuestionValueUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  /** Current user alias — recorded as `respondedBy` / `lastUpdatedBy`. */
  readonly alias: string;
  /** The data-collection sub-question the auditor just answered. */
  readonly subQuestionId: string;
  /**
   * The captured value — free text, number-as-string, selected
   * option, comma-joined multi-select, or employee alias. An empty
   * string means the auditor CLEARED the field: the value is omitted
   * on the wire (full-PATCH unset) while `responseType` is still
   * asserted so the sub stays typed.
   */
  readonly value: string;
  /**
   * The sub-question's data-collection response type (TEXT, NUMERIC,
   * SINGLE_SELECT, MULTI_SELECT, YES_NO, USER_ALIAS). Echoed onto the
   * wire so the persisted response records its own type. Callers must
   * only route data-collection types here; PASS_FAIL answers go
   * through {@link buildSubQuestionAnswerUpdate}.
   */
  readonly responseType: ResponseType;
  /**
   * The parent question's cached response, or undefined for a
   * never-answered parent. Comment / naReason / mediaIds / taskIds
   * survive the round-trip via {@link buildPreservedResponseData} —
   * identical to the Pass/Fail sub builder.
   */
  readonly previous?: QuestionResponseDetails | undefined;
  /**
   * Existing sub-question responses on the parent. Every entry
   * except the target is preserved as-is (result, comment, naReason,
   * mediaIds, taskIds, AND any sibling data-collection
   * responseType/responseValue); the target has its responseValue +
   * responseType overwritten while its other fields stay untouched.
   * Undefined / empty is fine — the target becomes the only entry.
   */
  readonly subQuestionResponses?: readonly SubQuestionResponseEntry[] | undefined;
  /**
   * Id + ERT responseType of every sub-question defined on the
   * parent. The length drives the `responseStatus` derivation — a
   * data-collection sub counts as answered once it carries a
   * non-empty value — and the types detect the SELF-SCORING case
   * (zero scored subs), where the parent's own `responseResult`
   * must be re-asserted on the wire (see docblock).
   */
  readonly subQuestionRefs: readonly SubQuestionTypeRef[];
}

/**
 * Builds the mutation input for a DATA-COLLECTION value save on a
 * sub-question (Enhanced Response Types). Mirrors
 * {@link buildSubQuestionAnswerUpdate} but writes the tapped value to
 * the target sub's `responseValue` (+ echoes `responseType`) instead
 * of a Pass/Fail `result`:
 *
 *   - Top-level `responseResult` is UNSET when the parent has any
 *     SCORED subs — the server owns that aggregation, same as the
 *     Pass/Fail sub builder. On a SELF-SCORING parent (zero scored
 *     subs — the auditor answers the parent directly) the cached
 *     parent result is RE-ASSERTED instead: the update is a
 *     full-PATCH, and the backend mapper overwrites the stored
 *     response before its derivation short-circuits, so omitting
 *     the field would silently clear the auditor's parent answer
 *     (V1 desktop's `saveQuestionResponse` documents the same
 *     preserve rule).
 *   - Top-level `responseStatus` is derived from the merged
 *     children treating a data-collection sub as answered when it
 *     carries a non-empty `responseValue` (a Pass/Fail sub is still
 *     answered by its `result`). PENDING while any child is missing
 *     or unanswered; COMPLETED otherwise. Exception: a SELF-SCORING
 *     parent (zero scored subs) whose cached parent result is NA
 *     receives status NA — matching the flat parent path — since
 *     `deriveSubResponsesStatus` has no scored children to infer it.
 *   - `subResponses` carries every child. The target has its
 *     `responseValue` + `responseType` set (value omitted when
 *     empty = cleared); non-target children pass through
 *     {@link projectSubResponsesForPreservation} unchanged.
 *
 * Pure — no react, no side effects. Consumed by the sub-question
 * value draft hook and its optimistic cache patch so both send the
 * same shape to the wire.
 */
export function buildSubQuestionValueUpdate(
  args: BuildSubQuestionValueUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const mergedSubResponses = mergeValueIntoSubResponses(
    args.subQuestionResponses,
    args.subQuestionId,
    args.value,
    args.responseType,
  );

  const derivedResponseStatus = deriveSubResponsesStatus(
    mergedSubResponses,
    args.subQuestionRefs.length,
  );

  // Self-scoring parent NA override: when there are zero scored subs the
  // parent scores itself directly, and the auditor can set the parent to NA.
  // deriveSubResponsesStatus always returns COMPLETED for a fully-valued
  // data-only set (it has no scored children to trigger the NA branch), so
  // we must override here — the flat parent path sets status = NA when
  // responseResult = NA, and the wire must match.
  const selfScoring = scoredDefs(args.subQuestionRefs).length === 0;
  const parentResult = args.previous?.responseResult;
  const responseStatus =
    selfScoring && parentResult === ResponseResult.NA
      ? ResponseStatus.NA
      : derivedResponseStatus;

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId: args.businessUnitId,
    auditId: args.auditId,
    questionId: args.questionId,
    responseStatus,
    respondedBy: args.alias,
    lastUpdatedBy: args.alias,
    subResponses: mergedSubResponses,
  };

  // Self-scoring parent (zero scored subs): re-assert the auditor's
  // parent answer so this full-PATCH doesn't wipe it server-side.
  // With scored subs present, the field stays unset — see docblock.
  if (selfScoring && parentResult !== undefined) {
    input.responseResult = parentResult;
  }

  const preservedResponse = buildPreservedResponseData(args.previous);
  if (preservedResponse) {
    input.response = preservedResponse;
  }

  return input;
}

/**
 * Merges the captured value into the projected sub-response array.
 * The projection preserves every child's fields (including sibling
 * data-collection values); the merge step only touches the TARGET
 * entry — overwriting its `responseValue` (omitted when the value is
 * empty = cleared) and asserting its `responseType`, while keeping
 * its comment / naReason / media / tasks intact — or appending a new
 * entry when the target has no prior response.
 */
function mergeValueIntoSubResponses(
  existing: readonly SubQuestionResponseEntry[] | undefined,
  targetSubQuestionId: string,
  value: string,
  responseType: ResponseType,
): SubResponse[] {
  const projected = projectSubResponsesForPreservation(existing);
  const hasValue = value !== '';

  const targetIndex = projected.findIndex(
    (entry) => entry.subQuestionId === targetSubQuestionId,
  );
  if (targetIndex >= 0) {
    return projected.map((entry, index) =>
      index === targetIndex
        ? applyValueToEntry(entry, value, responseType, hasValue)
        : entry,
    );
  }

  const created: SubResponse = { subQuestionId: targetSubQuestionId, responseType };
  if (hasValue) created.responseValue = value;
  return [...projected, created];
}

/**
 * Overwrites the target entry's data-collection fields immutably:
 * always asserts `responseType`; sets `responseValue` when non-empty
 * or strips it when cleared. All other preserved fields ride through.
 */
function applyValueToEntry(
  entry: SubResponse,
  value: string,
  responseType: ResponseType,
  hasValue: boolean,
): SubResponse {
  const next: SubResponse = { ...entry, responseType };
  if (hasValue) {
    next.responseValue = value;
  } else {
    // Cleared value — strip any stale one so the wire omits the
    // field (full-PATCH unset) rather than echoing the old answer.
    delete next.responseValue;
  }
  return next;
}
